import type {
  GoodsReceivedInput,
  SupplierGoodsInRow,
} from "@shared-core/data/supplier-goods-in-adapter";
import { goodsInOutstanding } from "@shared-core/domain/supplier-portal-wording";

/** What the warehouse fills in once for the deliveries it is recording: the reference and the date. */
export interface GoodsInFields {
  ref: string;
  date: string;
}

export function emptyGoodsInFields(today: string): GoodsInFields {
  return { ref: "", date: today };
}

/** A delivery can be recorded while goods are still due on it and the access may record them. */
export const isReceivable = (row: SupplierGoodsInRow) =>
  row.actions.includes("record_received");

/** The quantity a delivery starts at when it is ticked: all that is still due. */
export function startingQuantity(row: SupplierGoodsInRow): string {
  return String(goodsInOutstanding(row));
}

/** The wire entries for the ticked deliveries, each with its own quantity. A quantity that is not a number goes as
 *  zero, and the server says what is wrong with it (a rule of ours decides nothing here). */
export function receiptEntries(
  chosen: readonly SupplierGoodsInRow[],
  quantities: Record<string, string>,
  fields: GoodsInFields,
): GoodsReceivedInput[] {
  return chosen.map((row) => ({
    delivery_line_id: row.delivery_line_id,
    quantity:
      Number(quantities[row.delivery_line_id] ?? startingQuantity(row)) || 0,
    received_on: fields.date,
    goods_in_ref: fields.ref.trim(),
  }));
}

/** The form's own fields, which a refusal can name; anything else is shown as a plain line above the button. */
const FORM_FIELD_NAMES = ["goods_in_ref", "received_on"] as const;

export interface FoldedGoodsInErrors {
  /** By form field name: the first message wins, as every entry shares the form's reference and date. */
  form: Record<string, string>;
  /** By delivery line id: what is wrong with that row's quantity (or the row itself). */
  rows: Record<string, string>;
  /** Messages that belong to no input of the form. */
  unplaced: string[];
}

/**
 * A refusal names fields by the request body's path (`receipts.2.goods_in_ref`). The form has one reference and one
 * date for every entry it sent, so fold those onto their field; a quantity belongs to the row it was typed on (entry
 * `n` is the n-th ticked row); the rest are lines of their own.
 */
export function foldGoodsInErrors(
  fieldErrors: Record<string, string>,
  chosen: readonly SupplierGoodsInRow[],
): FoldedGoodsInErrors {
  const folded: FoldedGoodsInErrors = { form: {}, rows: {}, unplaced: [] };
  for (const [path, message] of Object.entries(fieldErrors)) {
    const match = /^receipts\.(\d+)\.(.+)$/.exec(path);
    const field = match ? match[2] : path;
    const row = match ? chosen[Number(match[1])] : undefined;
    if ((FORM_FIELD_NAMES as readonly string[]).includes(field)) {
      folded.form[field] ??= message;
    } else if (row) {
      folded.rows[row.delivery_line_id] ??= message;
    } else {
      folded.unplaced.push(message);
    }
  }
  return folded;
}
